import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMantineTableWithPaginationFilteringSorting } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import {
	ClassifierType,
	KYCMasterFilter,
	KYCWorkflowStatus,
} from '@dealstack/shared/lib/generated';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { useMipClassifiersList } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/hooks/useMipClassifiers';
import { GetKYCWorkflowRowRes } from '@dealstack/shared/lib/generated/models/GetKYCWorkflowRowRes';
import { KYCWorkflowsFilter } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/components/utils';
import { createKYCWorkflowsColumns } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/components/kycWorkflowsTableColumns';
import { useQuery } from '@tanstack/react-query';
import { getMipKYCWorkflows } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import {
	emptyToUndefined,
	toIdListFilterValue,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils.ts';

interface Props {
	onWithdrawStandalone?: (kycWorkflowId: string) => void;
	onKycClick?: (kycWorkflowId: string) => void;
	onInvestorDataClick?: (kycWorkflowId: string) => void;
	onTaxComplianceClick?: (kycWorkflowId: string) => void;
}

export const useKYCWorkflowsTable = (props?: Props) => {
	const { onWithdrawStandalone, onKycClick, onInvestorDataClick, onTaxComplianceClick } =
		props ?? {};
	const tMipManage = useTranslationMipManage();
	const navigate = useNavigate();
	const { mipId } = useRequiredParams('mipId');

	const { data: mipClassifiers = [] } = useMipClassifiersList({
		mipId,
		isArchived: false,
		classifiedType: ClassifierType.INVESTOR_COHORT,
		enabled: !!mipId,
	});
	const mipClassifierFilterOptions = useMemo(
		() => mipClassifiers.map((c) => ({ label: c.name, value: c.id })),
		[mipClassifiers]
	);

	const onInvestmentClick = useCallback(
		(investorName: string) => {
			navigate({
				pathname: `../..${MIP_MANAGE_ROUTES.INVESTMENTS}`,
				search: `?globalSearch=${encodeURIComponent(investorName)}`,
			});
		},
		[navigate]
	);
	const [queryParams, setQueryParams] = useState<{
		searchQuery?: string;
		status?: string[];
		offset?: number;
		limit?: number;
		declarationsStatus?: string[];
		proofOfIdentityStatus?: string[];
		proofOfAddressStatus?: string[];
		investmentVehicleStatus?: string[];
		formsStatus?: string[];
		investmentType?: string[];
		sortBy?: string;
		sortDesc?: boolean;
		masterFilter?: KYCMasterFilter;
		cohortIds?: string[];
		investorIds?: string[];
	}>({
		masterFilter: KYCMasterFilter.IN_PROGRESS,
		offset: 0,
		limit: 25,
	});

	const { data, isLoading } = useQuery({
		queryKey: [API_PATHS.MIP_KYC_WORKFLOWS_GET, queryParams],
		queryFn: ({ signal }) =>
			getMipKYCWorkflows(API_PATHS.MIP_KYC_WORKFLOWS_GET, queryParams, signal),
	});

	const workflows = data?.payload?.workflows ?? [];
	const rowCount = data?.payload?.totalRecord ?? 0;

	const columns = useMemo(
		() =>
			createKYCWorkflowsColumns({
				workflows,
				tMipManage,
				mipClassifierFilterOptions,
				onWithdrawStandalone,
				onKycClick,
				onInvestorDataClick,
				onTaxComplianceClick,
				onInvestmentClick,
			}),
		[
			workflows,
			mipClassifierFilterOptions,
			onKycClick,
			onInvestorDataClick,
			onTaxComplianceClick,
			onInvestmentClick,
		]
	);

	const { mantineTableDataOperations, dataOperations } =
		useMantineTableWithPaginationFilteringSorting<GetKYCWorkflowRowRes, KYCWorkflowsFilter>(
			columns,
			{
				initialPagination: { pageIndex: 0, pageSize: 25 },
				initialQuickFilters: { status: KYCWorkflowStatus.IN_PROGRESS },
			},
			true
		);

	const resolveMasterFilter = (quickStatus?: string): KYCMasterFilter | undefined => {
		if (quickStatus === KYCWorkflowStatus.IN_PROGRESS) {
			return KYCMasterFilter.IN_PROGRESS;
		}
		if (quickStatus === KYCWorkflowStatus.APPROVED) {
			return KYCMasterFilter.COMPLETED;
		}
		return undefined;
	};

	useEffect(() => {
		const { skip = 0, take = 25 } = dataOperations.pagination ?? {};
		const sort = dataOperations.sortBy?.[0];
		const columnStatus = dataOperations.filter?.status;

		setQueryParams({
			searchQuery: dataOperations.filter?.investor ?? dataOperations.search,
			investorIds: emptyToUndefined(toIdListFilterValue(dataOperations.filter?.investorId)),
			status: columnStatus?.length ? columnStatus : undefined,
			masterFilter: resolveMasterFilter(
				dataOperations.quickFilters?.status as string | undefined
			),
			offset: skip,
			limit: take,
			declarationsStatus: dataOperations.filter?.declarationsStatus,
			proofOfIdentityStatus: dataOperations.filter?.proofOfIdentityStatus,
			proofOfAddressStatus: dataOperations.filter?.proofOfAddressStatus,
			investmentVehicleStatus: dataOperations.filter?.investmentVehicleStatus,
			formsStatus: dataOperations.filter?.formsStatus,
			investmentType: dataOperations.filter?.investmentType,
			cohortIds: dataOperations.filter?.cohorts,
			sortBy: sort?.key,
			sortDesc: sort?.desc,
		});
	}, [dataOperations]);

	return {
		columns,
		mantineTableDataOperations,
		workflows,
		isLoading,
		rowCount,
		// The exact filter set the list is showing — reused by "select all" bulk download so
		// the server resolves the same matching workflows across every page.
		downloadFilters: queryParams,
	};
};
